import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef, useMemo } from 'react';
import { Room, RoomFilters, RoomContext as RoomContextType, RoomPlayer, normalizeRoomCategory } from '../types/user';
import { signalRService, ConnectionState } from '../services/signalr/SignalRService';
import { HubMethods, HubEvents, SignalRRoom, SignalRPlayer, JoinRequestInfo } from '../services/signalr/types';
import { emit as emitTelemetry, newCorrelationId, nowMs } from '../services/telemetry/FlexoTelemetry';

interface RoomContextValue {
  rooms: Room[];
  currentRoom: Room | null;
  filters: RoomFilters;
  filteredRooms: Room[];
  isLoading: boolean;
  isConnected: boolean;
  error: string | null;
  pendingJoinRequests: JoinRequestInfo[];
  createRoom: (room: Omit<Room, 'id' | 'createdAt' | 'currentPlayers' | 'players' | 'status'>) => Promise<Room>;
  joinRoom: (roomId: string, userId: string, displayName: string, avatar?: string, characterType?: string, gender?: string, password?: string, appearanceData?: string) => Promise<boolean>;
  leaveRoom: (roomId: string, userId: string) => Promise<void>;
  setFilters: (filters: Partial<RoomFilters>) => void;
  setCurrentRoom: (room: Room | null) => void;
  refreshRooms: () => Promise<void>;
  /** Fast path for /room/:code deep links: fetch ONE room instead of the full
      list. Returns 'found' | 'not-found' | 'error' (error ⇒ caller should fall
      back to refreshRooms, e.g. when the backend lacks the hub method). */
  findRoomByCode: (code: string) => Promise<'found' | 'not-found' | 'error'>;
  requestToJoin: (roomId: string, userId: string, displayName: string, avatar?: string, message?: string) => Promise<JoinRequestInfo>;
  approveJoinRequest: (requestId: string) => Promise<void>;
  denyJoinRequest: (requestId: string, reason?: string) => Promise<void>;
  cancelJoinRequest: (requestId: string) => Promise<void>;
  getPendingJoinRequests: (roomId: string) => Promise<JoinRequestInfo[]>;
}

const RoomContext = createContext<RoomContextValue | undefined>(undefined);

export function RoomProvider({ children }: { children: ReactNode }) {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [currentRoom, setCurrentRoom] = useState<Room | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingJoinRequests, setPendingJoinRequests] = useState<JoinRequestInfo[]>([]);
  // Keep currentRoomIdRef in sync with currentRoom for telemetry handlers.
  // (the ref is declared below alongside other refs)
  const [filters, setFiltersState] = useState<RoomFilters>({
    search: '',
    context: 'all',
    minAge: 18,
    maxAge: 99,
    showFull: false,
    showPrivate: true,
  });

  const cleanupRef = useRef<(() => void)[]>([]);
  const listenersSetupRef = useRef(false);
  const hasFetchedRoomsRef = useRef(false);
  // AC 8.1: telemetry on disconnect/reconnect must carry roomId + playerId.
  // Track them in refs so the SignalR event handlers (declared in a one-shot
  // useEffect) always see the latest values without re-binding.
  const currentRoomIdRef = useRef<string | null>(null);
  const currentPlayerIdRef = useRef<string | null>(null);

  useEffect(() => {
    currentRoomIdRef.current = currentRoom?.id ?? null;
  }, [currentRoom?.id]);

  // Set up SignalR event listeners (only once)
  const setupListeners = useCallback(() => {
    // Prevent duplicate listener setup
    if (listenersSetupRef.current) return;
    listenersSetupRef.current = true;

    const unsubscribers: (() => void)[] = [];

    // Room list updated
    unsubscribers.push(
      signalRService.on<[SignalRRoom[]]>(HubEvents.RoomListUpdated, (signalRRooms) => {
        const mappedRooms = signalRRooms.map(mapSignalRRoomToRoom);
        setRooms(mappedRooms);
      })
    );

    // Room created
    unsubscribers.push(
      signalRService.on<[SignalRRoom]>(HubEvents.RoomCreated, (signalRRoom) => {
        const room = mapSignalRRoomToRoom(signalRRoom);
        setRooms((prev) => {
          // Avoid duplicates
          if (prev.find((r) => r.id === room.id)) return prev;
          return [room, ...prev];
        });
      })
    );

    // Room updated
    unsubscribers.push(
      signalRService.on<[SignalRRoom]>(HubEvents.RoomUpdated, (signalRRoom) => {
        const room = mapSignalRRoomToRoom(signalRRoom);
        setRooms((prev) => prev.map((r) => (r.id === room.id ? room : r)));
        setCurrentRoom((prev) => (prev?.id === room.id ? room : prev));
      })
    );

    // Room deleted
    unsubscribers.push(
      signalRService.on<[string]>(HubEvents.RoomDeleted, (roomId) => {
        setRooms((prev) => prev.filter((r) => r.id !== roomId));
        setCurrentRoom((prev) => (prev?.id === roomId ? null : prev));
      })
    );

    // Player joined room - server sends playerJson as a string for Unity compatibility.
    // PlayerReconnected uses the same shape and the same upsert: a player who grace-left
    // and rejoined must reappear in the React roster (gift/throw pickers read it) — the
    // Unity bridge already folds PlayerReconnected into PlayerJoinedRoom for the avatar
    // side; without this the two rosters drift (found in Cove P2 prod verification).
    const upsertRoomPlayer = (roomId: string, playerData: string | SignalRPlayer, eventName: string) => {
      // Parse JSON string if needed (server sends string for Unity compatibility)
      let signalRPlayer: SignalRPlayer;
      if (typeof playerData === 'string') {
        try {
          signalRPlayer = JSON.parse(playerData) as SignalRPlayer;
        } catch (e) {
          console.error(`[RoomContext] Failed to parse ${eventName} data:`, e);
          return;
        }
      } else {
        signalRPlayer = playerData as SignalRPlayer;
      }

      console.log(`[RoomContext] ${eventName}:`, roomId, signalRPlayer);
      const player = mapSignalRPlayerToRoomPlayer(signalRPlayer);
      setRooms((prev) =>
        prev.map((r) => {
          if (r.id === roomId) {
            const existingPlayers = r.players.filter((p) => p.id !== player.id);
            const newPlayers = [...existingPlayers, player];
            return {
              ...r,
              players: newPlayers,
              currentPlayers: newPlayers.length,
              status: newPlayers.length >= r.maxPlayers ? 'full' : r.status === 'full' ? 'waiting' : r.status,
            };
          }
          return r;
        })
      );
      setCurrentRoom((prev) => {
        if (prev?.id === roomId) {
          const existingPlayers = prev.players.filter((p) => p.id !== player.id);
          const newPlayers = [...existingPlayers, player];
          return {
            ...prev,
            players: newPlayers,
            currentPlayers: newPlayers.length,
            status: newPlayers.length >= prev.maxPlayers ? 'full' : prev.status === 'full' ? 'waiting' : prev.status,
          };
        }
        return prev;
      });
    };

    unsubscribers.push(
      signalRService.on<[string, string | SignalRPlayer]>(HubEvents.PlayerJoinedRoom, (roomId, playerData) =>
        upsertRoomPlayer(roomId, playerData, 'PlayerJoinedRoom'))
    );
    unsubscribers.push(
      signalRService.on<[string, string | SignalRPlayer]>('PlayerReconnected', (roomId, playerData) =>
        upsertRoomPlayer(roomId, playerData, 'PlayerReconnected'))
    );

    // Player left room
    unsubscribers.push(
      signalRService.on<[string, string]>(HubEvents.PlayerLeftRoom, (roomId, playerId) => {
        setRooms((prev) =>
          prev.map((r) => {
            if (r.id === roomId) {
              const newPlayers = r.players.filter((p) => p.id !== playerId);
              return {
                ...r,
                players: newPlayers,
                currentPlayers: newPlayers.length,
                status: newPlayers.length < r.maxPlayers && r.status === 'full' ? 'waiting' : r.status,
              };
            }
            return r;
          })
        );
        setCurrentRoom((prev) => {
          if (prev?.id === roomId) {
            const newPlayers = prev.players.filter((p) => p.id !== playerId);
            return {
              ...prev,
              players: newPlayers,
              currentPlayers: newPlayers.length,
              status: newPlayers.length < prev.maxPlayers && prev.status === 'full' ? 'waiting' : prev.status,
            };
          }
          return prev;
        });
      })
    );

    // Join request received (for hosts)
    unsubscribers.push(
      signalRService.on<[string, JoinRequestInfo]>(HubEvents.JoinRequestReceived, (_roomId, request) => {
        console.log('[RoomContext] JoinRequestReceived:', request);
        setPendingJoinRequests((prev) => {
          // Avoid duplicates
          if (prev.find((r) => r.id === request.id)) return prev;
          return [...prev, request];
        });
      })
    );

    // Join request cancelled
    unsubscribers.push(
      signalRService.on<[string, string]>(HubEvents.JoinRequestCancelled, (_roomId, requestId) => {
        console.log('[RoomContext] JoinRequestCancelled:', requestId);
        setPendingJoinRequests((prev) => prev.filter((r) => r.id !== requestId));
      })
    );

    cleanupRef.current = unsubscribers;
  }, []);

  // Handle connection state changes via SignalR event handlers
  useEffect(() => {
    const handleConnectionStateChange = (state: ConnectionState) => {
      const connected = state === 'connected';
      setIsConnected(connected);

      if (connected) {
        setupListeners();
        // Only fetch rooms once per connection, not repeatedly
        if (!hasFetchedRoomsRef.current) {
          hasFetchedRoomsRef.current = true;
          fetchRooms();
        }
      } else if (state === 'disconnected' || state === 'error') {
        hasFetchedRoomsRef.current = false;
      }
    };

    // Set up connection state handler
    signalRService.setEventHandlers({
      onConnectionStateChange: handleConnectionStateChange,
      onReconnected: () => {
        setIsConnected(true);
        emitTelemetry({
          event: 'signalr_resumed',
          roomId: currentRoomIdRef.current,
          playerId: currentPlayerIdRef.current,
          correlationId: newCorrelationId(),
          ts: nowMs(),
        });
        // Re-fetch rooms after reconnection
        fetchRooms();
      },
      onReconnecting: () => {
        setIsConnected(false);
      },
      onDisconnected: () => {
        emitTelemetry({
          event: 'signalr_disconnected',
          roomId: currentRoomIdRef.current,
          playerId: currentPlayerIdRef.current,
          correlationId: newCorrelationId(),
          ts: nowMs(),
        });
      },
    });

    // Check initial connection state (only once)
    const initialConnected = signalRService.isConnected;
    setIsConnected(initialConnected);
    if (initialConnected) {
      setupListeners();
      fetchRooms();
      hasFetchedRoomsRef.current = true;
    } else {
      setIsLoading(false);
    }

    return () => {
      cleanupRef.current.forEach((unsub) => unsub());
      listenersSetupRef.current = false;
      hasFetchedRoomsRef.current = false;
    };
  }, [setupListeners]);

  const fetchRooms = async () => {
    if (!signalRService.isConnected) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const signalRRooms = await signalRService.invoke<SignalRRoom[]>(HubMethods.GetRooms);
      const mappedRooms = signalRRooms.map(mapSignalRRoomToRoom);
      setRooms(mappedRooms);
    } catch (e) {
      console.error('Failed to fetch rooms:', e);
      setError('Failed to load rooms');
    } finally {
      setIsLoading(false);
    }
  };

  // Memoize filtered rooms to prevent unnecessary recalculations
  const filteredRooms = useMemo(() => {
    return rooms.filter((room) => {
      if (filters.search && !room.name.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      if (filters.context !== 'all' && room.context !== filters.context) {
        return false;
      }
      if (room.minAge > filters.maxAge || room.maxAge < filters.minAge) {
        return false;
      }
      if (!filters.showFull && room.status === 'full') {
        return false;
      }
      if (!filters.showPrivate && room.isPrivate) {
        return false;
      }
      return true;
    });
  }, [rooms, filters]);

  const createRoom = useCallback(
    async (roomData: Omit<Room, 'id' | 'createdAt' | 'currentPlayers' | 'players' | 'status'>): Promise<Room> => {
      if (!signalRService.isConnected) {
        throw new Error('Not connected to server');
      }

      try {
        const signalRRoomData = {
          name: roomData.name,
          description: roomData.description,
          hostId: roomData.hostId,
          hostName: roomData.hostName,
          maxPlayers: roomData.maxPlayers,
          context: roomData.context,
          tags: roomData.tags,
          isPrivate: roomData.isPrivate,
          password: roomData.password,
          sceneTemplateId: roomData.sceneTemplateId || undefined,
          settings: {
            minAge: roomData.minAge,
            maxAge: roomData.maxAge,
          },
        };

        const signalRRoom = await signalRService.invoke<SignalRRoom>(HubMethods.CreateRoom, signalRRoomData);
        const newRoom = mapSignalRRoomToRoom(signalRRoom);

        // Optimistically add to local state (server will also broadcast)
        setRooms((prev) => [newRoom, ...prev]);
        return newRoom;
      } catch (e) {
        console.error('Failed to create room:', e);
        throw e;
      }
    },
    []
  );

  const joinRoom = useCallback(
    async (roomId: string, userId: string, displayName: string, avatar?: string, characterType?: string, gender?: string, password?: string, appearanceData?: string): Promise<boolean> => {
      if (!signalRService.isConnected) {
        console.error('Not connected to server');
        emitTelemetry({
          event: 'signalr_disconnected',
          roomId,
          playerId: userId,
          correlationId: newCorrelationId(),
          ts: nowMs(),
          payload: { reason: 'not_connected_at_join' },
        });
        return false;
      }

      const correlationId = newCorrelationId();
      currentPlayerIdRef.current = userId;
      emitTelemetry({
        event: 'room_join_requested',
        roomId,
        playerId: userId,
        correlationId,
        ts: nowMs(),
      });

      try {
        // NOTE: RoomHub.JoinRoom(roomId, playerInfo) takes exactly 2 params —
        // passing correlationId as a 3rd arg makes the server reject the
        // invocation with "too many arguments". correlationId stays local
        // (telemetry only).
        const signalRRoom = await signalRService.invoke<SignalRRoom>(HubMethods.JoinRoom, roomId, {
          id: userId,
          displayName,
          avatar,
          characterType,
          gender,
          password,
          appearanceData,
        });

        emitTelemetry({
          event: 'signalr_subscribed',
          roomId,
          playerId: userId,
          correlationId,
          ts: nowMs(),
        });

        const room = mapSignalRRoomToRoom(signalRRoom);
        setCurrentRoom(room);

        // Update local rooms list
        setRooms((prev) => prev.map((r) => (r.id === room.id ? room : r)));

        return true;
      } catch (e: unknown) {
        const error = e as Error;
        // Check for password-related errors
        if (error.message?.includes('PASSWORD_REQUIRED')) {
          throw new Error('PASSWORD_REQUIRED');
        }
        if (error.message?.includes('INVALID_PASSWORD')) {
          throw new Error('INVALID_PASSWORD');
        }
        // Check for approval-required error
        if (error.message?.includes('APPROVAL_REQUIRED')) {
          throw new Error('APPROVAL_REQUIRED');
        }
        console.error('Failed to join room:', e);
        emitTelemetry({
          event: 'bridge_handshake_failed',
          roomId,
          playerId: userId,
          correlationId,
          ts: nowMs(),
          payload: { reason: 'invoke_failed', message: error.message },
        });
        throw e;
      }
    },
    []
  );

  const leaveRoom = useCallback(async (roomId: string, userId: string) => {
    if (!signalRService.isConnected) {
      console.error('Not connected to server');
      return;
    }

    try {
      await signalRService.invoke(HubMethods.LeaveRoom, roomId);
      setCurrentRoom(null);
      currentPlayerIdRef.current = null;

      // Optimistically update local state
      setRooms((prev) =>
        prev.map((r) => {
          if (r.id === roomId) {
            const newPlayers = r.players.filter((p) => p.id !== userId);
            return {
              ...r,
              players: newPlayers,
              currentPlayers: newPlayers.length,
              status: newPlayers.length < r.maxPlayers ? 'waiting' : r.status,
            };
          }
          return r;
        })
      );
    } catch (e) {
      console.error('Failed to leave room:', e);
    }
  }, []);

  const setFilters = useCallback((newFilters: Partial<RoomFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const refreshRooms = useCallback(async () => {
    await fetchRooms();
  }, []);

  const findRoomByCode = useCallback(async (code: string): Promise<'found' | 'not-found' | 'error'> => {
    if (!signalRService.isConnected) return 'error';
    try {
      const signalRRoom = await signalRService.invoke<SignalRRoom | null>('GetRoomByCode', code);
      if (!signalRRoom) return 'not-found';
      const mapped = mapSignalRRoomToRoom(signalRRoom);
      setRooms((prev) => [...prev.filter((r) => r.id !== mapped.id), mapped]);
      return 'found';
    } catch (e) {
      console.warn('[RoomContext] GetRoomByCode failed (falling back to full list):', e);
      return 'error';
    }
  }, []);

  // Join Request (Knock-to-Enter) methods
  const requestToJoin = useCallback(
    async (roomId: string, userId: string, displayName: string, avatar?: string, message?: string): Promise<JoinRequestInfo> => {
      if (!signalRService.isConnected) {
        throw new Error('Not connected to server');
      }

      try {
        const request = await signalRService.invoke<JoinRequestInfo>(HubMethods.RequestToJoin, roomId, {
          id: userId,
          displayName,
          avatar,
        }, message);
        return request;
      } catch (e) {
        console.error('Failed to request to join room:', e);
        throw e;
      }
    },
    []
  );

  const approveJoinRequest = useCallback(
    async (requestId: string): Promise<void> => {
      if (!signalRService.isConnected) {
        throw new Error('Not connected to server');
      }

      try {
        const room = await signalRService.invoke<SignalRRoom>(HubMethods.ApproveJoinRequest, requestId);
        const mappedRoom = mapSignalRRoomToRoom(room);
        setCurrentRoom(mappedRoom);
        setRooms((prev) => prev.map((r) => (r.id === mappedRoom.id ? mappedRoom : r)));
        setPendingJoinRequests((prev) => prev.filter((r) => r.id !== requestId));
      } catch (e) {
        console.error('Failed to approve join request:', e);
        throw e;
      }
    },
    []
  );

  const denyJoinRequest = useCallback(
    async (requestId: string, reason?: string): Promise<void> => {
      if (!signalRService.isConnected) {
        throw new Error('Not connected to server');
      }

      try {
        await signalRService.invoke(HubMethods.DenyJoinRequest, requestId, reason);
        setPendingJoinRequests((prev) => prev.filter((r) => r.id !== requestId));
      } catch (e) {
        console.error('Failed to deny join request:', e);
        throw e;
      }
    },
    []
  );

  const cancelJoinRequest = useCallback(
    async (requestId: string): Promise<void> => {
      if (!signalRService.isConnected) {
        throw new Error('Not connected to server');
      }

      try {
        await signalRService.invoke(HubMethods.CancelJoinRequest, requestId);
      } catch (e) {
        console.error('Failed to cancel join request:', e);
        throw e;
      }
    },
    []
  );

  const getPendingJoinRequests = useCallback(
    async (roomId: string): Promise<JoinRequestInfo[]> => {
      if (!signalRService.isConnected) {
        throw new Error('Not connected to server');
      }

      try {
        const requests = await signalRService.invoke<JoinRequestInfo[]>(HubMethods.GetPendingJoinRequests, roomId);
        setPendingJoinRequests(requests);
        return requests;
      } catch (e) {
        console.error('Failed to get pending join requests:', e);
        throw e;
      }
    },
    []
  );

  return (
    <RoomContext.Provider
      value={{
        rooms,
        currentRoom,
        filters,
        filteredRooms,
        isLoading,
        isConnected,
        error,
        pendingJoinRequests,
        createRoom,
        joinRoom,
        leaveRoom,
        setFilters,
        setCurrentRoom,
        refreshRooms,
        findRoomByCode,
        requestToJoin,
        approveJoinRequest,
        denyJoinRequest,
        cancelJoinRequest,
        getPendingJoinRequests,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
}

export function useRooms() {
  const context = useContext(RoomContext);
  if (!context) {
    throw new Error('useRooms must be used within a RoomProvider');
  }
  return context;
}

// Helper functions to map between SignalR types and app types
function mapSignalRRoomToRoom(signalRRoom: SignalRRoom): Room {
  return {
    id: signalRRoom.id,
    code: signalRRoom.code ?? '',
    name: signalRRoom.name,
    description: signalRRoom.description,
    context: signalRRoom.context as RoomContextType,
    hostId: signalRRoom.hostId,
    hostName: signalRRoom.hostName,
    minAge: signalRRoom.settings?.minAge ?? 18,
    maxAge: signalRRoom.settings?.maxAge ?? 99,
    maxPlayers: signalRRoom.maxPlayers,
    currentPlayers: signalRRoom.currentPlayers,
    players: (signalRRoom.players ?? []).map(mapSignalRPlayerToRoomPlayer),
    tags: signalRRoom.tags ?? [],
    isPrivate: signalRRoom.isPrivate,
    hasPassword: signalRRoom.hasPassword ?? false,
    requiresApproval: signalRRoom.requiresApproval ?? false,
    createdAt: new Date(signalRRoom.createdAt),
    status: signalRRoom.status,
    category: normalizeRoomCategory(signalRRoom.category),
    subCategory: signalRRoom.subCategory,
    sceneTemplateId: signalRRoom.sceneTemplateId,
  };
}

function mapSignalRPlayerToRoomPlayer(signalRPlayer: SignalRPlayer): RoomPlayer {
  return {
    id: signalRPlayer.id,
    displayName: signalRPlayer.displayName,
    avatar: signalRPlayer.avatar,
    characterType: signalRPlayer.characterType,
    gender: signalRPlayer.gender as 'male' | 'female' | undefined,
    isHost: signalRPlayer.isHost,
    joinedAt: new Date(signalRPlayer.joinedAt),
    // Chat role fields - default host to dungeon_master, others to user
    chatRole: signalRPlayer.chatRole ?? (signalRPlayer.isHost ? 'dungeon_master' : 'user'),
    chatAvailable: signalRPlayer.chatAvailable ?? true,
  };
}
